import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KOGSection from '@/main/components/shared/kogSection/kogSection';
import { KeyValue } from '@/main/models/common';
import { SupplierInventoryDataResponse } from '@/main/models/response';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import { getDefaultCountryOptionDrop, getDefaultOptionDrop, getTranslateByKey, isNullUndefined } from '@/main/services/utils';
import { useCountryReducer } from '@/main/store/country';
import { getSuppliers, resetReducerSuppliers, useSupplierReducer } from '@/main/store/supplier';
import { getWarehouse, resetReducerWarehouse, useWarehouseReducer } from '@/main/store/warehouse';

import { IInventoryRegisterForm } from '../utilsInventoryRegisterScreen';
import * as S from './styles';

interface ISectionSupplierProps {
  dataInventory: SupplierInventoryDataResponse | null;
  watcherCountry: null | KeyValue;
  isEdit: boolean;
}
function SectionSupplier({
  dataInventory,
  isEdit,
  watcherCountry
}: ISectionSupplierProps) {
  // #region Reducers
  const { countriesUser: countryReducer } = useCountryReducer();
  const { suppliers: supplierReducer } = useSupplierReducer();
  const { warehouse: warehouseReducer } = useWarehouseReducer();
  const optionDefaultSupplier = getDefaultOptionDrop(supplierReducer.data);
  // #endregion

  // #region Hooks
  const dispatch = useDispatch();
  const {
    control,
    resetField,
    setValue,
  } = useFormContext<IInventoryRegisterForm>();
  const watcherSupplier = useWatch({
    name: 'supplier',
    control,
  })
  // #endregion

  // #region Async Functions
  async function callSupplierApiDomain(countrySelected: KeyValue | null) {
    if (!isEdit) {
      resetField('supplier')
      resetField('warehouse')
      dispatch(resetReducerSuppliers())
      dispatch(resetReducerWarehouse())
    }

    if (!isNullUndefined(countrySelected)) {
      dispatch(getSuppliers({ guidCountry: String(countrySelected.key), idStatusRegister: StatusRegisterEnum.Active }))
    }
  }

  async function callWarehouseApiDomain(supplierSelected: KeyValue | null) {
    if (!isEdit) {
      resetField('warehouse')
      dispatch(resetReducerWarehouse())
    }

    if (!isNullUndefined(supplierSelected)) {
      dispatch(getWarehouse({ guidSupplier: String(supplierSelected.key) }))
    }
  }
  // #endregion

  // #region UseEffects
  useEffect(() => {
    return () => {
      dispatch(resetReducerSuppliers())
      dispatch(resetReducerWarehouse())
    }
  }, [])

  useEffect(() => {
    callSupplierApiDomain(watcherCountry)
  }, [watcherCountry?.key])

  useEffect(() => {
    callWarehouseApiDomain(watcherSupplier)
  }, [watcherSupplier?.key])
  // #endregion

  // #region Constants Layout
  const defaultCountry = (isEdit && !isNullUndefined(dataInventory)) ? dataInventory.guidCountry : getDefaultCountryOptionDrop(countryReducer.data)?.key;
  const defaultSupplier = (isEdit && !isNullUndefined(dataInventory)) ? dataInventory.guidSupplier : optionDefaultSupplier?.key;
  const defaultWarehouse = (isEdit && !isNullUndefined(dataInventory)) ? dataInventory.guidWarehouse : null;
  const disabledCountry = isEdit || (!isNullUndefined(countryReducer.data) && countryReducer.data.length === 1);
  const disabledSupplier = isEdit || supplierReducer.isLoading || !isNullUndefined(optionDefaultSupplier);
  const disabledWarehouse = isEdit || isNullUndefined(warehouseReducer.data) || warehouseReducer.data.length === 1;
  // #endregion

  const titleSection = getTranslateByKey('information.supplierInformation');

  return (
    <KOGSection title={titleSection}>
      <S.Container>
        <KogAutoCompleteControlled
          id="id-inventoryRegister-select-country"
          nameCustom="country"
          label={getTranslateByKey('information.country')}
          valueDefaultKey={defaultCountry}
          control={control}
          options={countryReducer.data}
          isLoading={countryReducer.isLoading}
          rulesCustom={{ required: true }}
          disabled={disabledCountry}
          setValueCustom={setValue}
          onChangeCustom={() => { }}
        />
        <KogAutoCompleteControlled
          id="id-inventoryRegister-select-supplier"
          nameCustom="supplier"
          label={getTranslateByKey('information.supplier')}
          valueDefaultKey={defaultSupplier}
          isDefaultValueUnique
          control={control}
          options={supplierReducer.data}
          isLoading={supplierReducer.isLoading}
          disabled={disabledSupplier}
          rulesCustom={{ required: true }}
          setValueCustom={setValue}
          onChangeCustom={() => { }}
        />
        <KogAutoCompleteControlled
          id="id-inventoryRegister-select-warehouse"
          nameCustom="warehouse"
          isDefaultValueUnique
          label={getTranslateByKey('information.warehouse')}
          valueDefaultKey={defaultWarehouse}
          control={control}
          options={warehouseReducer.data}
          isLoading={warehouseReducer.isLoading}
          disabled={disabledWarehouse}
          rulesCustom={{ required: true }}
          setValueCustom={setValue}
          onChangeCustom={() => { }}
        />

      </S.Container>
    </KOGSection>
  )
}

export default SectionSupplier